import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  FileText,
  Info,
  Loader2,
  Search,
  User as UsuarioIcono,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Cargando } from '@/shared/ui/Cargando';
import type { Pagina } from '@/shared/api/cliente';
import type { Cliente, CuentaDeServicio, DeudaParaCobrar } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { notificar } from '@/shared/ui/avisos';

export function PaginaConsultaDeDeuda() {
  const [busqueda, setBusqueda] = useState('');
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [cuenta, setCuenta] = useState('');
  const [bajando, setBajando] = useState(false);

  const clientes = useQuery({
    queryKey: ['clientes', 'consulta-deuda', busqueda],
    queryFn: () =>
      api.get<Pagina<Cliente>>('/clientes', { query: { texto: busqueda, tamanio: 8 } }),
    enabled: busqueda.trim().length >= 3,
  });

  // Los predios del contribuyente, para poder preguntar por uno solo.
  const cuentas = useQuery({
    queryKey: ['cuentas-del-cliente', cliente?.id],
    queryFn: () => api.get<CuentaDeServicio[]>(`/clientes/${cliente?.id}/cuentas`),
    enabled: cliente !== null,
  });

  const deuda = useQuery({
    queryKey: ['deuda-para-cobrar', cliente?.id, cuenta],
    queryFn: () =>
      api.get<DeudaParaCobrar>(`/cobros/clientes/${cliente?.id}`, {
        query: cuenta ? { cuenta } : {},
      }),
    enabled: cliente !== null,
  });

  async function descargar() {
    if (!cliente) {
      return;
    }
    setBajando(true);
    try {
      const pdf = await api.descargar(
        `/cobros/clientes/${cliente.id}/estado-de-cuenta.pdf${cuenta ? `?cuenta=${cuenta}` : ''}`,
      );
      if (!pdf) {
        notificar.error('NO SE PUDO GENERAR EL ESTADO DE CUENTA');
        return;
      }

      const url = URL.createObjectURL(pdf);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `estado-de-cuenta-${cliente.identificacion}.pdf`;
      enlace.click();
      URL.revokeObjectURL(url);

      notificar.exito('Estado de cuenta descargado');
    } catch (causa) {
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DESCARGAR EL ESTADO DE CUENTA',
      );
    } finally {
      setBajando(false);
    }
  }

  function elegir(c: Cliente) {
    setCliente(c);
    // El predio elegido es de la persona anterior: dejarlo puesto consultaria una cuenta
    // que no es suya y la pantalla saldria vacia sin decir por que.
    setCuenta('');
  }

  const d = deuda.data;

  return (
    <>
      <EncabezadoDePagina
        icono={Search}
        titulo="Consulta de deudas"
        subtitulo="Qué debe una persona, desde cuándo y por qué predio"
      />

      <div className="mb-5 rounded-2xl border border-armazon-200 bg-white p-5">
        <label className="grid gap-2">
          <span className="text-sm font-semibold uppercase text-armazon-900">
            Buscar a quien pregunta
          </span>
          <span className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre, cédula, RUC, cuenta o medidor…"
              className="h-11 w-full rounded-lg border border-armazon-200 bg-white pl-9 pr-3 text-sm outline-none transition-colors focus:border-marca-400"
            />
          </span>
          <span className="text-xs uppercase text-armazon-500">Desde tres letras o dígitos</span>
        </label>

        {busqueda.trim().length >= 3 && (
          <ul className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-armazon-200">
            {clientes.isLoading && (
              <li className="px-3 py-2 text-xs uppercase text-armazon-400">Buscando…</li>
            )}
            {clientes.data?.contenido.length === 0 && (
              <li className="px-3 py-2 text-xs uppercase text-armazon-400">
                Ningún cliente coincide
              </li>
            )}
            {(clientes.data?.contenido ?? []).map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => elegir(c)}
                  className={`flex w-full items-center gap-2.5 border-b border-armazon-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-armazon-50 ${
                    cliente?.id === c.id ? 'bg-marca-50' : ''
                  }`}
                >
                  <Icono identificacion={c.identificacion} />
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-medium uppercase text-armazon-800">
                      {c.nombre}
                    </span>
                    <span className="importe block text-[10px] text-armazon-500">
                      {c.identificacion}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {cliente === null ? (
        <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
          Busque a la persona para ver qué debe
        </p>
      ) : deuda.isPending ? (
        <Cargando
          titulo="Consultando la deuda"
          detalle="Buscando sus facturas sin pagar"
          alto="h-64"
        />
      ) : (
        d && (
          <>
            {(cuentas.data?.length ?? 0) > 1 && (
              <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-armazon-200 bg-white p-4">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-armazon-600">
                  Predio
                </span>
                <select
                  value={cuenta}
                  onChange={(e) => setCuenta(e.target.value)}
                  className="h-10 min-w-72 rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none focus:border-marca-400"
                >
                  <option value="">Todos los predios de la persona</option>
                  {(cuentas.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.numeroCuenta}
                      {c.direccionServicio ? ` · ${c.direccionServicio}` : ''}
                    </option>
                  ))}
                </select>
                {cuenta && (
                  <span className="text-[11px] uppercase text-amber-700">
                    Se muestra lo que debe este predio. El cobro es por factura completa.
                  </span>
                )}
              </div>
            )}

            <div className="mb-5 rounded-2xl border border-armazon-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 gap-3">
                  <Icono identificacion={d.identificacion} grande />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold uppercase text-armazon-800">
                      {d.nombre}
                    </p>
                    <p className="importe text-xs text-armazon-500">{d.identificacion}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  {d.facturas.length === 0 ? (
                    <p className="flex items-center gap-2 text-sm font-bold uppercase text-marca-700">
                      <CheckCircle2 className="size-4" aria-hidden />
                      No debe nada
                    </p>
                  ) : (
                    <>
                      <span className="text-right">
                        <span className="importe block text-2xl font-bold text-armazon-800">
                          ${d.total.toFixed(2)}
                        </span>
                        <span className="block text-[10px] uppercase text-armazon-500">
                          {d.facturas.length} facturas · servicio ${d.capital.toFixed(2)}
                          {d.interes > 0 && (
                            <span className="text-amber-700">
                              {' '}
                              · interés ${d.interes.toFixed(2)}
                            </span>
                          )}
                        </span>
                      </span>

                      <button
                        type="button"
                        onClick={() => void descargar()}
                        disabled={bajando}
                        title="Descargar el estado de cuenta en PDF"
                        aria-label="Descargar el estado de cuenta en PDF"
                        className="grid size-10 shrink-0 place-items-center rounded-lg border border-armazon-200 text-armazon-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-40"
                      >
                        {bajando ? (
                          <Loader2 className="size-[18px] animate-spin" aria-hidden />
                        ) : (
                          <FileText className="size-[18px]" aria-hidden />
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {d.facturas.length > 0 && !d.tasaAlDia && (
                <p className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>
                    <span className="font-semibold">
                      La tasa de mora no está actualizada al trimestre de hoy.
                    </span>{' '}
                    El interés está calculado al {d.tasaMensual} % mensual, que es la última
                    cargada.
                  </span>
                </p>
              )}

              {d.facturas.length > 0 && d.exentoInteresMora && (
                <p className="mt-4 flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-blue-800">
                  <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>
                    <span className="font-semibold">
                      Este cliente está exento de interés por mora.
                    </span>{' '}
                    Solo se le cobra el servicio.
                  </span>
                </p>
              )}
            </div>

            {d.facturas.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
                <div className="hidden items-center gap-4 border-b border-armazon-100 bg-armazon-50/60 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400 lg:flex">
                  <span className="w-20 shrink-0">Periodo</span>
                  <span className="min-w-0 flex-1 basis-56">Predio</span>
                  <span className="w-24 shrink-0">Venció</span>
                  <span className="w-28 shrink-0 text-right">Servicio</span>
                  <span className="w-28 shrink-0 text-right">Interés</span>
                  <span className="w-24 shrink-0 text-right">A pagar</span>
                </div>

                <div className="divide-y divide-armazon-100">
                  {d.facturas.map((f) => (
                    <div
                      key={`${f.historica}-${f.id}`}
                      className="flex flex-wrap items-center gap-4 px-6 py-3 transition-colors hover:bg-armazon-50/60"
                    >
                      <span className="importe w-20 shrink-0 text-xs font-semibold text-armazon-800">
                        {f.periodo}
                      </span>

                      <span className="min-w-0 flex-1 basis-56">
                        {f.numeroCuenta ? (
                          <>
                            <span className="importe block truncate text-xs text-armazon-700">
                              {f.numeroCuenta}
                            </span>
                            <span className="block truncate text-[10px] uppercase text-armazon-400">
                              {f.direccionServicio}
                            </span>
                          </>
                        ) : (
                          <span className="text-[11px] uppercase text-armazon-400">
                            Todos sus predios
                          </span>
                        )}
                      </span>

                      <span className="importe w-24 shrink-0 text-[11px] text-armazon-600">
                        {formatearCorto(f.vencimiento)}
                      </span>

                      <span className="importe w-28 shrink-0 text-right text-[11px] text-armazon-600">
                        ${f.capital.toFixed(2)}
                      </span>

                      <span className="w-28 shrink-0 text-right">
                        {f.interes > 0 ? (
                          <>
                            <span className="importe block text-[11px] font-semibold text-amber-700">
                              ${f.interes.toFixed(2)}
                            </span>
                            <span className="block text-[9px] uppercase text-armazon-400">
                              {f.mesesMora} {f.mesesMora === 1 ? 'mes' : 'meses'}
                            </span>
                          </>
                        ) : (
                          <span className="text-[11px] text-armazon-300">—</span>
                        )}
                      </span>

                      <span className="importe w-24 shrink-0 text-right text-sm font-semibold text-armazon-800">
                        ${f.total.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )
      )}
    </>
  );
}

function Icono({
  identificacion,
  grande = false,
}: {
  identificacion: string | null;
  grande?: boolean;
}) {
  const esInstitucion = (identificacion ?? '').length === 13;
  const caja = grande ? 'size-10' : 'size-7';
  const trazo = grande ? 'size-5' : 'size-3.5';

  return (
    <span
      className={`grid ${caja} shrink-0 place-items-center rounded-lg ${
        esInstitucion ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-500'
      }`}
    >
      {esInstitucion ? (
        <Building2 className={trazo} aria-hidden />
      ) : (
        <UsuarioIcono className={trazo} aria-hidden />
      )}
    </span>
  );
}

function formatearCorto(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
